import { appointmentResourceOnlyExecutionMatches, readAppointmentResourceOnlyExecutionV1, readAppointmentResourceOnlyExecutionsV1, type AppointmentResourceOnlyExecutionV1 } from "@/app/booking/core/appointmentResourceOnlyExecutionV1";
import {
  getSpecialistAssignmentByRoute,
  getSpecialistLocationAppointmentCatalogPublication,
  getSpecialistLocationTeamAssignments,
  getSpecialistOfferingsForAssignment,
  getSpecialistPerson,
  getSpecialistServicesForAssignment,
  getSpecialistWorkplace,
} from "@/app/specialists/catalog";
import type {
  SpecialistAssignment,
  SpecialistLocationAppointmentCatalogPublication,
  SpecialistLocationTeamAssignment,
  SpecialistPerson,
  SpecialistService,
  SpecialistServiceOffering,
  SpecialistWorkplace,
} from "@/app/specialists/model";
import { isSpecialistOfferingOnlineBookableAtWorkplace } from "@/app/specialists/onlineBooking";
import { getSpecialistServiceBundle } from "@/app/specialists/serviceBundles";

const MAX_IDENTIFIER_LENGTH = 160;
const MAX_PRESENTATION_TEXT_LENGTH = 500;

export interface LocationAppointmentProfessional {
  userLocationId: string;
  name: string;
  avatarSrc: string | null;
  avatarAlt: string;
  role: string | null;
  category: string;
  reviewSummary: {
    averageRating: number | null;
    count: number;
  };
}

export interface LocationAppointmentEligibilityPublicationV1 {
  resourceOnlyExecutionsV1?: readonly AppointmentResourceOnlyExecutionV1[];
  schemaVersion: 1;
  providerId: string;
  providerRevision: string;
  locationId: string;
  catalogVersion: string;
  catalogOwnerUserLocationId: string;
  rows: readonly {
    offeringId: string;
    professionalUserLocationIds: readonly string[];
  }[];
}

export type LocationAppointmentService = SpecialistService & {
  readonly serviceVersion: string;
};

/**
 * Route-independent, provider-validated Location appointment catalog.
 * `catalogAssignment` owns the canonical service/offering snapshots used by
 * Step 1. `professionals` are presentation only; task eligibility must be
 * resolved through `resolveLocationAppointmentTaskProfessionals`.
 */
export interface LocationAppointmentCatalog {
  workplace: SpecialistWorkplace;
  catalogAssignment: SpecialistAssignment;
  services: readonly LocationAppointmentService[];
  offerings: readonly SpecialistServiceOffering[];
  professionals: readonly LocationAppointmentProfessional[];
  /** Serializable eligibility evidence resolved with this exact catalog. */
  eligibilityPublication: LocationAppointmentEligibilityPublicationV1;
}

export interface LocationAppointmentTaskIdentity {
  serviceId: string;
  offeringId: string;
}

export interface LocationAppointmentCatalogSource {
  getPublication: (
    locationId: string,
  ) => SpecialistLocationAppointmentCatalogPublication | undefined;
  getWorkplace: (locationId: string) => SpecialistWorkplace | undefined;
  getAssignmentByRoute: (
    locationId: string,
    userLocationId: string,
  ) => SpecialistAssignment | undefined;
  getServicesForAssignment: (
    userLocationId: string,
  ) => readonly SpecialistService[];
  getOfferingsForAssignment: (
    userLocationId: string,
  ) => readonly SpecialistServiceOffering[];
  getTeamAssignmentsForLocation: (
    locationId: string,
  ) => readonly SpecialistLocationTeamAssignment[];
  getPerson: (personId: string) => SpecialistPerson | undefined;
}

export const locationAppointmentCatalogFixtureSource: LocationAppointmentCatalogSource =
  {
    getPublication:
      getSpecialistLocationAppointmentCatalogPublication,
    getWorkplace: getSpecialistWorkplace,
    getAssignmentByRoute: getSpecialistAssignmentByRoute,
    getServicesForAssignment: getSpecialistServicesForAssignment,
    getOfferingsForAssignment: getSpecialistOfferingsForAssignment,
    getTeamAssignmentsForLocation:
      getSpecialistLocationTeamAssignments,
    getPerson: getSpecialistPerson,
  };

interface ResolvedLocationAppointmentCatalog {
  catalog: LocationAppointmentCatalog;
  eligibleProfessionalIdsByOfferingId: ReadonlyMap<
    string,
    readonly string[]
  >;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isCanonicalIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= MAX_IDENTIFIER_LENGTH
  );
}

function isPresentationText(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= MAX_PRESENTATION_TEXT_LENGTH
  );
}

function hasUniqueValues(values: readonly string[]) {
  return new Set(values).size === values.length;
}

function hasSameStructure(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true;
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) =>
        hasSameStructure(value, right[index]),
      )
    );
  }

  if (!isRecord(left) || !isRecord(right)) {
    return false;
  }

  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();

  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key, index) =>
        key === rightKeys[index] &&
        hasSameStructure(left[key], right[key]),
    )
  );
}

function isValidProfessionalPresentation(person: SpecialistPerson) {
  const { reviewSummary } = person;

  return (
    isCanonicalIdentifier(person.personId) &&
    isPresentationText(person.name) &&
    (person.avatarSrc === null || isPresentationText(person.avatarSrc)) &&
    isPresentationText(person.avatarAlt) &&
    (person.role === null || isPresentationText(person.role)) &&
    isPresentationText(person.category) &&
    Number.isSafeInteger(reviewSummary.count) &&
    reviewSummary.count >= 0 &&
    (reviewSummary.averageRating === null ||
      (Number.isFinite(reviewSummary.averageRating) &&
        reviewSummary.averageRating >= 0 &&
        reviewSummary.averageRating <= 5))
  );
}

function hasCompleteCanonicalCatalog(input: {
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  publication: SpecialistLocationAppointmentCatalogPublication;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
}) {
  const { assignment, workplace, publication, services, offerings } = input;
  const executions = readAppointmentResourceOnlyExecutionsV1(publication);
  if (executions === null || !Array.isArray(offerings)) return false;
  const expected = offerings.flatMap(offering => {
    const proof = readAppointmentResourceOnlyExecutionV1(offering);
    return proof === undefined ? [] : [proof];
  });
  if (
    expected.some(proof => !proof) ||
    (executions?.length ?? 0) !== expected.length ||
    expected.some((proof, index) =>
      !proof || JSON.stringify(proof) !== JSON.stringify(executions?.[index]) ||
      !appointmentResourceOnlyExecutionMatches({
        evidence: proof,
        providerId: publication.providerId,
        providerRevision: publication.revision,
        locationId: publication.locationId,
        catalogVersion: publication.catalogVersion,
        offeringId: proof.offeringId,
        requiredResourceIds: offerings.find(row => row.offeringId === proof.offeringId)?.requiredResourceIds ?? [],
      }),
    )
  ) {
    return false;
  }
  const serviceIds = services.map(({ serviceId }) => serviceId);
  const offeringIds = offerings.map(({ offeringId }) => offeringId);

  if (
    workplace.locationId !== publication.locationId ||
    workplace.catalogVersion !== publication.catalogVersion ||
    assignment.locationId !== publication.locationId ||
    assignment.userLocationId !==
      publication.catalogOwnerUserLocationId ||
    assignment.serviceIds.length === 0 ||
    !hasUniqueValues(assignment.serviceIds) ||
    services.length !== assignment.serviceIds.length ||
    offerings.length !== assignment.serviceIds.length ||
    !hasUniqueValues(serviceIds) ||
    !hasUniqueValues(offeringIds)
  ) {
    return false;
  }

  const serviceById = new Map(
    services.map((service) => [service.serviceId, service]),
  );
  const offeringByServiceId = new Map(
    offerings.map((offering) => [offering.serviceId, offering]),
  );

  if (offeringByServiceId.size !== offerings.length) {
    return false;
  }

  return assignment.serviceIds.every((serviceId, index) => {
    const service = services[index];
    const offering = offerings[index];

    if (
      !service ||
      !offering ||
      service.serviceId !== serviceId ||
      offering.serviceId !== serviceId ||
      offering.order !== index ||
      offering.userLocationId !== assignment.userLocationId ||
      offering.catalogVersion !== publication.catalogVersion ||
      !isCanonicalIdentifier(service.serviceId) ||
      !isCanonicalIdentifier(service.serviceVersion) ||
      !isPresentationText(service.name) ||
      !isCanonicalIdentifier(service.categoryId) ||
      !isCanonicalIdentifier(offering.offeringId) ||
      !isSpecialistOfferingOnlineBookableAtWorkplace(
        assignment,
        workplace,
        offering,
        executions ?? undefined,
      )
    ) {
      return false;
    }

    if (service.kind !== "bundle") {
      return service.kind === "service";
    }

    const bundle = getSpecialistServiceBundle(service);

    return Boolean(
      bundle &&
        bundle.items.every(
          (item) =>
            serviceById.has(item.serviceId) &&
            offeringByServiceId.has(item.serviceId),
        ),
    );
  });
}

function resolveLocationAppointmentCatalogInternal(
  locationId: string,
  source: LocationAppointmentCatalogSource,
): ResolvedLocationAppointmentCatalog | null {
  if (!isCanonicalIdentifier(locationId)) {
    return null;
  }

  const publication = source.getPublication(locationId);

  if (
    !publication ||
    publication.locationId !== locationId ||
    !isCanonicalIdentifier(publication.providerId) ||
    !isCanonicalIdentifier(publication.revision) ||
    !publication.isPublished ||
    !isCanonicalIdentifier(publication.catalogVersion) ||
    !isCanonicalIdentifier(publication.catalogOwnerUserLocationId) ||
    (publication.publicTeamUserLocationIds.length === 0 && !readAppointmentResourceOnlyExecutionsV1(publication)) ||
    !hasUniqueValues(publication.publicTeamUserLocationIds) ||
    publication.publicTeamUserLocationIds.some(
      (userLocationId) => !isCanonicalIdentifier(userLocationId),
    )
  ) {
    return null;
  }

  const workplace = source.getWorkplace(locationId);
  const catalogAssignment = source.getAssignmentByRoute(
    locationId,
    publication.catalogOwnerUserLocationId,
  );

  if (!workplace || !catalogAssignment) {
    return null;
  }

  const services = source.getServicesForAssignment(
    catalogAssignment.userLocationId,
  );
  const offerings = source.getOfferingsForAssignment(
    catalogAssignment.userLocationId,
  );

  if (
    !hasCompleteCanonicalCatalog({
      assignment: catalogAssignment,
      workplace,
      publication,
      services,
      offerings,
    })
  ) {
    return null;
  }

  const teamAssignments = source.getTeamAssignmentsForLocation(locationId);

  if (
    teamAssignments.length !==
      publication.publicTeamUserLocationIds.length ||
    teamAssignments.some(
      (assignment, index) =>
        assignment.userLocationId !==
        publication.publicTeamUserLocationIds[index],
    )
  ) {
    return null;
  }

  const offeringIds = new Set(
    offerings.map((offering) => offering.offeringId),
  );
  const professionalIds = new Set<string>();
  const personIds = new Set<string>();
  const professionals: LocationAppointmentProfessional[] = [];
  const eligibleProfessionalIdsByOfferingId = new Map<
    string,
    string[]
  >();

  for (const [order, assignment] of teamAssignments.entries()) {
    const person = source.getPerson(assignment.personId);

    if (
      assignment.locationId !== locationId ||
      assignment.order !== order ||
      (assignment.userLocationId === catalogAssignment.userLocationId &&
        assignment.personId !== catalogAssignment.personId) ||
      professionalIds.has(assignment.userLocationId) ||
      personIds.has(assignment.personId) ||
      !assignment.isPubliclyVisible ||
      !assignment.isOnlineBookable ||
      !assignment.calendarBookingsEnabled ||
      typeof assignment.isAutoAssignable !== "boolean" ||
      assignment.catalogVersion !== publication.catalogVersion ||
      !isCanonicalIdentifier(assignment.presentationRevision) ||
      !isCanonicalIdentifier(assignment.eligibilityRevision) ||
      assignment.eligibleOfferingIds.length === 0 ||
      !hasUniqueValues(assignment.eligibleOfferingIds) ||
      assignment.eligibleOfferingIds.some(
        (offeringId) => !offeringIds.has(offeringId) || Boolean(offerings.find(row => row.offeringId === offeringId)?.resourceOnlyExecutionV1),
      ) ||
      !person ||
      !isValidProfessionalPresentation(person)
    ) {
      return null;
    }

    professionalIds.add(assignment.userLocationId);
    personIds.add(assignment.personId);
    professionals.push({
      userLocationId: assignment.userLocationId,
      name: person.name,
      avatarSrc: person.avatarSrc,
      avatarAlt: person.avatarAlt,
      role: person.role,
      category: person.category,
      reviewSummary: { ...person.reviewSummary },
    });

    assignment.eligibleOfferingIds.forEach((offeringId) => {
      const eligibleIds =
        eligibleProfessionalIdsByOfferingId.get(offeringId) ?? [];

      eligibleIds.push(assignment.userLocationId);
      eligibleProfessionalIdsByOfferingId.set(offeringId, eligibleIds);
    });
  }

  if (
    offerings.some(
      (offering) =>
        !offering.resourceOnlyExecutionV1 && (eligibleProfessionalIdsByOfferingId.get(offering.offeringId)
          ?.length ?? 0) === 0,
    )
  ) {
    return null;
  }

  return {
    catalog: {
      workplace,
      catalogAssignment,
      services: services.map((service) => ({
        ...service,
        serviceVersion: service.serviceVersion!,
      })),
      offerings: [...offerings],
      professionals,
      eligibilityPublication: {
        schemaVersion: 1,
        ...(publication.resourceOnlyExecutionsV1 ? { resourceOnlyExecutionsV1: readAppointmentResourceOnlyExecutionsV1(publication)! } : {}),
        providerId: publication.providerId,
        providerRevision: publication.revision,
        locationId,
        catalogVersion: publication.catalogVersion,
        catalogOwnerUserLocationId:
          publication.catalogOwnerUserLocationId,
        rows: offerings.map((offering) => ({
          offeringId: offering.offeringId,
          professionalUserLocationIds: [
            ...(eligibleProfessionalIdsByOfferingId.get(
              offering.offeringId,
            ) ?? []),
          ],
        })),
      },
    },
    eligibleProfessionalIdsByOfferingId,
  };
}

export function resolveLocationAppointmentCatalog(
  locationId: string,
  source: LocationAppointmentCatalogSource =
    locationAppointmentCatalogFixtureSource,
): LocationAppointmentCatalog | null {
  return resolveLocationAppointmentCatalogInternal(locationId, source)?.catalog ?? null;
}

function isValidEmbeddedProfessional(
  value: unknown,
): value is LocationAppointmentProfessional {
  if (
    !isRecord(value) ||
    Object.keys(value).sort().join("|") !==
      "avatarAlt|avatarSrc|category|name|reviewSummary|role|userLocationId" ||
    !isCanonicalIdentifier(value.userLocationId) ||
    !isPresentationText(value.name) ||
    (value.avatarSrc !== null && !isPresentationText(value.avatarSrc)) ||
    !isPresentationText(value.avatarAlt) ||
    (value.role !== null && !isPresentationText(value.role)) ||
    !isPresentationText(value.category) ||
    !isRecord(value.reviewSummary) ||
    Object.keys(value.reviewSummary).sort().join("|") !==
      "averageRating|count"
  ) {
    return false;
  }

  const { averageRating, count } = value.reviewSummary;

  return (
    Number.isSafeInteger(count) &&
    (count as number) >= 0 &&
    (averageRating === null ||
      (typeof averageRating === "number" &&
        Number.isFinite(averageRating) &&
        averageRating >= 0 &&
        averageRating <= 5))
  );
}

function resolveEmbeddedEligibilityUnsafe(
  catalog: LocationAppointmentCatalog,
) {
  const publication = catalog.eligibilityPublication;
  if (
    !Array.isArray(catalog.services) ||
    !Array.isArray(catalog.offerings) ||
    !Array.isArray(catalog.professionals) ||
    (catalog.professionals.length === 0 && !readAppointmentResourceOnlyExecutionsV1(publication)) ||
    catalog.professionals.some(
      (professional) => !isValidEmbeddedProfessional(professional),
    )
  ) {
    return null;
  }

  const professionalIds = new Set(
    catalog.professionals.map(({ userLocationId }) => userLocationId),
  );

  if (
    !isRecord(publication) ||
    Object.keys(publication).filter(key => key !== "resourceOnlyExecutionsV1").sort().join("|") !==
      "catalogOwnerUserLocationId|catalogVersion|locationId|providerId|providerRevision|rows|schemaVersion" ||
    publication.schemaVersion !== 1 ||
    publication.locationId !== catalog.workplace.locationId ||
    publication.catalogVersion !== catalog.workplace.catalogVersion ||
    publication.catalogOwnerUserLocationId !==
      catalog.catalogAssignment.userLocationId ||
    !isCanonicalIdentifier(publication.providerId) ||
    !isCanonicalIdentifier(publication.providerRevision) ||
    professionalIds.size !== catalog.professionals.length ||
    !hasCompleteCanonicalCatalog({
      assignment: catalog.catalogAssignment,
      workplace: catalog.workplace,
      publication: {
        locationId: publication.locationId,
        providerId: publication.providerId,
        revision: publication.providerRevision,
        isPublished: true,
        catalogVersion: publication.catalogVersion,
        catalogOwnerUserLocationId:
          publication.catalogOwnerUserLocationId,
        ...(publication.resourceOnlyExecutionsV1 ? { resourceOnlyExecutionsV1: publication.resourceOnlyExecutionsV1 } : {}),
        publicTeamUserLocationIds: catalog.professionals.map(
          ({ userLocationId }) => userLocationId,
        ),
      },
      services: catalog.services,
      offerings: catalog.offerings,
    }) ||
    !Array.isArray(publication.rows) ||
    publication.rows.length !== catalog.offerings.length
  ) {
    return null;
  }

  const eligibility = new Map<string, readonly string[]>();
  const referencedProfessionalIds = new Set<string>();

  for (const [index, offering] of catalog.offerings.entries()) {
    const row = publication.rows[index];

    if (
      !isRecord(row) ||
      Object.keys(row).sort().join("|") !==
        "offeringId|professionalUserLocationIds" ||
      row.offeringId !== offering.offeringId ||
      !Array.isArray(row.professionalUserLocationIds) ||
      (offering.resourceOnlyExecutionV1 ? row.professionalUserLocationIds.length !== 0 : row.professionalUserLocationIds.length === 0) ||
      !hasUniqueValues(row.professionalUserLocationIds) ||
      row.professionalUserLocationIds.some(
        (userLocationId) =>
          !isCanonicalIdentifier(userLocationId) ||
          !professionalIds.has(userLocationId),
      )
    ) {
      return null;
    }

    eligibility.set(
      offering.offeringId,
      [...row.professionalUserLocationIds],
    );
    row.professionalUserLocationIds.forEach((userLocationId) =>
      referencedProfessionalIds.add(userLocationId),
    );
  }

  return referencedProfessionalIds.size === professionalIds.size &&
    [...professionalIds].every((userLocationId) =>
      referencedProfessionalIds.has(userLocationId),
    )
    ? eligibility
    : null;
}

function resolveEmbeddedEligibility(
  catalog: LocationAppointmentCatalog,
) {
  try {
    return resolveEmbeddedEligibilityUnsafe(catalog);
  } catch {
    return null;
  }
}

/**
 * Validates the complete serializable Location eligibility snapshot without
 * consulting a fixture or provider again. Every owner/version, canonical
 * offering row, and referenced professional must belong to this exact catalog.
 */
export function validateLocationAppointmentCatalogEmbeddedEligibility(
  catalog: LocationAppointmentCatalog,
) {
  return resolveEmbeddedEligibility(catalog) !== null;
}

/**
 * Resolves one task from route-owned, serializable eligibility evidence. The
 * server route has already joined this snapshot to the exact catalog; client
 * coordinators must consume it instead of re-looking up a fixture source.
 */
export function resolveLocationAppointmentTaskProfessionalsFromCatalog(
  catalog: LocationAppointmentCatalog,
  task: LocationAppointmentTaskIdentity,
): readonly LocationAppointmentProfessional[] | null {
  const offering = catalog.offerings.find(
    (candidate) => candidate.offeringId === task.offeringId,
  );
  const eligibility = resolveEmbeddedEligibility(catalog);

  if (!offering || offering.serviceId !== task.serviceId || !eligibility) {
    return null;
  }

  const eligibleIds = eligibility.get(task.offeringId);
  if (!eligibleIds) return null;

  const professionalById = new Map(
    catalog.professionals.map((professional) => [
      professional.userLocationId,
      professional,
    ]),
  );
  const professionals = eligibleIds.map((userLocationId) =>
    professionalById.get(userLocationId),
  );

  return professionals.every(
    (professional): professional is LocationAppointmentProfessional =>
      Boolean(professional),
  )
    ? professionals
    : null;
}

/**
 * Resolves the public professional options for one exact scheduling task.
 * Both service and offering identity are required so a stale or cross-service
 * task cannot inherit eligibility from another catalog row.
 */
export function resolveLocationAppointmentTaskProfessionals(
  catalog: LocationAppointmentCatalog,
  task: LocationAppointmentTaskIdentity,
  source: LocationAppointmentCatalogSource =
    locationAppointmentCatalogFixtureSource,
): readonly LocationAppointmentProfessional[] | null {
  const current = resolveLocationAppointmentCatalogInternal(
    catalog.workplace.locationId,
    source,
  );

  if (!current || !hasSameStructure(catalog, current.catalog)) {
    return null;
  }

  return resolveLocationAppointmentTaskProfessionalsFromCatalog(
    current.catalog,
    task,
  );
}

/**
 * Shared all-task gate for a Location-owned initial professional. Presentation
 * and draft creation must agree: a single name may be claimed only when the
 * exact current catalog authorizes that professional for every requested task.
 */
export function isLocationAppointmentProfessionalEligibleForTasks(
  catalog: LocationAppointmentCatalog,
  tasks: readonly LocationAppointmentTaskIdentity[],
  userLocationId: string,
  source: LocationAppointmentCatalogSource =
    locationAppointmentCatalogFixtureSource,
) {
  const current = resolveLocationAppointmentCatalogInternal(
    catalog.workplace.locationId,
    source,
  );

  return Boolean(
    current &&
      hasSameStructure(catalog, current.catalog) &&
      isLocationAppointmentProfessionalEligibleForTasksFromCatalog(
        current.catalog,
        tasks,
        userLocationId,
      ),
  );
}

export function isLocationAppointmentProfessionalEligibleForTasksFromCatalog(
  catalog: LocationAppointmentCatalog,
  tasks: readonly LocationAppointmentTaskIdentity[],
  userLocationId: string,
) {
  if (!isCanonicalIdentifier(userLocationId) || tasks.length === 0) {
    return false;
  }

  return tasks.every((task) =>
    resolveLocationAppointmentTaskProfessionalsFromCatalog(
      catalog,
      task,
    )?.some(
      (professional) =>
        professional.userLocationId === userLocationId,
    ),
  );
}

/**
 * Exact entry gate for Location Group. A workplace flag alone is not enough:
 * at least one ordinary published task must expose two distinct eligible
 * professionals so two parallel participants are not structurally forced
 * onto the same calendar.
 */
export function isLocationGroupBookingEntryFeasible(
  catalog: LocationAppointmentCatalog,
  source: LocationAppointmentCatalogSource =
    locationAppointmentCatalogFixtureSource,
) {
  const current = resolveLocationAppointmentCatalogInternal(
    catalog.workplace.locationId,
    source,
  );

  return Boolean(
    current &&
      hasSameStructure(catalog, current.catalog) &&
      isLocationGroupBookingEntryFeasibleFromCatalog(current.catalog),
  );
}

export function isLocationGroupBookingEntryFeasibleFromCatalog(
  catalog: LocationAppointmentCatalog,
) {
  const policy = catalog.workplace.groupBookingPolicy;

  if (!policy?.enabled || policy.maximumParticipants < 2) {
    return false;
  }

  return catalog.services.some((service) => {
    if (service.kind !== "service") return false;
    const offering = catalog.offerings.find(
      (candidate) => candidate.serviceId === service.serviceId,
    );
    if (!offering) return false;

    const professionals = resolveLocationAppointmentTaskProfessionalsFromCatalog(
      catalog,
      {
        serviceId: service.serviceId,
        offeringId: offering.offeringId,
      },
    );

    return Boolean(
      professionals &&
        new Set(
          professionals.map(({ userLocationId }) => userLocationId),
        ).size >= 2,
    );
  });
}
